import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { supabase, supabaseUrl, supabaseKey } from './supabaseClient';
import Home from './pages/Home';
import Vault from './pages/Vault';
import Login from './pages/Login';
import Subscribe from './pages/Subscribe';
import Dashboard from './pages/Dashboard.js';
import Quiz from './pages/Quiz';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import Welcome from './pages/Welcome';
import Privacy from './pages/Privacy';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import './App.css';
if (process.env.NODE_ENV === 'development') import('./_testQuizStats');

const SUB_CACHE_KEY = 'trp_sub_cache';

function readSubCache(userId) {
  try {
    const raw = localStorage.getItem(SUB_CACHE_KEY);
    if (!raw) return null;
    const { uid, status, periodEnd, trialEnd } = JSON.parse(raw);
    if (uid !== userId) return null;
    const isPaidStatus = status === 'active' || status === 'trialing';
    const hasValidPeriod = !!periodEnd && new Date(periodEnd) > new Date();
    const trialEndsAt = trialEnd || null;
    const hasActiveTrial = !!trialEndsAt && new Date(trialEndsAt) > new Date();
    const hasFullAccess = (isPaidStatus && hasValidPeriod) || hasActiveTrial;
    if (!hasFullAccess) return null;
    return { isSubscribed: isPaidStatus && hasValidPeriod, hasFullAccess, trialEndsAt };
  } catch (_) { return null; }
}

function writeSubCache(userId, status, periodEnd, trialEnd) {
  try { localStorage.setItem(SUB_CACHE_KEY, JSON.stringify({ uid: userId, status, periodEnd, trialEnd })); } catch (_) {}
}

function clearSubCache() {
  try { localStorage.removeItem(SUB_CACHE_KEY); } catch (_) {}
}

// Uses direct fetch instead of supabase client to bypass the Web Lock that
// the auth system holds during token refresh — which caused queries via the
// supabase client to deadlock on hard refresh.
async function fetchIsSubscribed(userId, accessToken) {
  try {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(
      `${supabaseUrl}/rest/v1/subscriptions?select=status,current_period_end,trial_ends_at&user_id=eq.${userId}&limit=1`,
      {
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${accessToken}`,
        },
        signal: controller.signal,
      }
    );
    clearTimeout(t);
    const rows = await res.json();
    const row = Array.isArray(rows) ? rows[0] : null;
    const isPaidStatus = row?.status === 'active' || row?.status === 'trialing';
    const hasValidPeriod = !!row?.current_period_end && new Date(row.current_period_end) > new Date();
    const isSubscribed = isPaidStatus && hasValidPeriod;
    const trialEndsAt = row?.trial_ends_at || null;
    const hasActiveTrial = !!trialEndsAt && new Date(trialEndsAt) > new Date();
    const hasFullAccess = isSubscribed || hasActiveTrial;
    if (hasFullAccess) writeSubCache(userId, row?.status, row?.current_period_end, trialEndsAt);
    else clearSubCache();
    return { isSubscribed, hasFullAccess, trialEndsAt };
  } catch (_) {
    return readSubCache(userId) || { isSubscribed: false, hasFullAccess: false, trialEndsAt: null };
  }
}

// Shown briefly after Stripe redirect while webhook is still processing.
function ArcFlashRouteRedirect() {
  const location = useLocation();
  const target = `/quiz${location.search || ''}`;
  return <Navigate to={target} replace state={{ arcFlashView: true }} />;
}

function VerifyingPayment({ session, onVerified, onGiveUp }) {
  useEffect(() => {
    let attempts = 0;
    const MAX = 20; // poll up to 40 seconds
    let timerId;

    const poll = async () => {
      try {
        const result = await fetchIsSubscribed(session.user.id, session.access_token);
        if (result.isSubscribed) { onVerified(); return; }
      } catch (_) {}
      attempts++;
      if (attempts < MAX) {
        timerId = setTimeout(poll, 2000);
      } else {
        onGiveUp();
      }
    };

    timerId = setTimeout(poll, 2000);
    return () => clearTimeout(timerId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="loading" style={{ flexDirection: 'column', gap: '1rem' }}>
      <div style={{ fontSize: '2rem' }}>⚡</div>
      <div>Verifying your payment…</div>
      <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>This usually takes a few seconds.</div>
    </div>
  );
}

function App() {
  const [session, setSession]           = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [trialEndsAt, setTrialEndsAt]   = useState(null);
  const [appReady, setAppReady]         = useState(false);
  const [darkMode, setDarkMode]         = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : '');
  }, [darkMode]);

  useEffect(() => {
    if ((isSubscribed || hasFullAccess) && session) {
      sessionStorage.removeItem('trp_pending_checkout');
    }
  }, [isSubscribed, hasFullAccess, session]);

  useEffect(() => {
    // Hard deadline — app always becomes ready after 5s no matter what.
    const hardDeadline = setTimeout(() => setAppReady(true), 5000);

    // Soft fallback — if onAuthStateChange doesn't fire within 2s (lock conflict),
    // try getSession independently. getSession runs fire-and-forget so a hang
    // there can't block the hard deadline above.
    const softFallback = setTimeout(() => {
      supabase.auth.getSession().then(async ({ data: { session: s } }) => {
        if (s) {
          setSession(s);
          const result = await fetchIsSubscribed(s.user.id, s.access_token);
          setIsSubscribed(result.isSubscribed);
          setHasFullAccess(result.hasFullAccess);
          setTrialEndsAt(result.trialEndsAt);
        }
        clearTimeout(hardDeadline);
        setAppReady(true);
      }).catch(() => {
        clearTimeout(hardDeadline);
        setAppReady(true);
      });
    }, 2000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, newSession) => {
        clearTimeout(softFallback); // cancel soft fallback — we're handling it
        // hardDeadline is intentionally NOT cleared here — if fetchIsSubscribed
        // hangs, the hard deadline still fires and unblocks the loading screen.
        if (!newSession) {
          setSession(null);
          setIsSubscribed(false);
          setHasFullAccess(false);
          setTrialEndsAt(null);
        } else {
          setSession(newSession);
          try {
            let result = await fetchIsSubscribed(newSession.user.id, newSession.access_token);

            const pendingPromo = sessionStorage.getItem('trp_pending_promo');
            if (pendingPromo) {
              sessionStorage.removeItem('trp_pending_promo');
              try {
                const rpcRes = await fetch(`${supabaseUrl}/rest/v1/rpc/redeem_promo_code`, {
                  method: 'POST',
                  headers: {
                    'apikey': supabaseKey,
                    'Authorization': `Bearer ${newSession.access_token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    code_to_redeem: pendingPromo,
                    user_id_to_set: newSession.user.id,
                  }),
                });
                const rpcData = await rpcRes.json();
                if (rpcData?.success) {
                  result = await fetchIsSubscribed(newSession.user.id, newSession.access_token);
                }
              } catch (_) {}
            }

            setIsSubscribed(result.isSubscribed);
            setHasFullAccess(result.hasFullAccess);
            setTrialEndsAt(result.trialEndsAt);
          } catch (_) {
            setIsSubscribed(false);
            setHasFullAccess(false);
            setTrialEndsAt(null);
          }
        }
        clearTimeout(hardDeadline);
        setAppReady(true);
      }
    );

    return () => {
      subscription.unsubscribe();
      clearTimeout(softFallback);
      clearTimeout(hardDeadline);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!appReady) return <div className="loading">Loading…</div>;

  const toggleDark = () => setDarkMode(d => !d);
  const sharedProps = { darkMode, toggleDark, isSubscribed, hasFullAccess, trialEndsAt };

  // Coming back from Stripe — poll until webhook writes the subscription.
  if (session && window.location.search.includes('welcome=true')) {
    return <VerifyingPayment
      session={session}
      onVerified={() => { window.location.replace('/welcome'); }}
      onGiveUp={() => { window.location.href = '/quiz'; }}
    />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/"
          element={session ? <Navigate to="/quiz" /> : <Home session={session} {...sharedProps} />}
        />
        <Route path="/home"
          element={<Home session={session} {...sharedProps} />}
        />
        <Route path="/login"
          element={!session ? <Login {...sharedProps} /> : <Navigate to="/quiz" />}
        />
        <Route path="/subscribe"
          element={!session ? <Subscribe {...sharedProps} /> : <Navigate to="/quiz" />}
        />
        {/* Legacy redirect — old /signup links still work */}
        <Route path="/signup" element={<Navigate to="/subscribe" />} />
        <Route path="/welcome"
          element={
            session && isSubscribed
              ? <Welcome session={session} {...sharedProps} />
              : <Navigate to="/" />
          }
        />
        <Route path="/quiz"
          element={<Quiz session={session} {...sharedProps} />}
        />
        <Route path="/arc-flash"
          element={<ArcFlashRouteRedirect />}
        />
        <Route path="/vault"
          element={<Vault session={session} {...sharedProps} />}
        />
        <Route path="/dashboard"
          element={
            session
              ? <Dashboard session={session} {...sharedProps} />
              : <Navigate to="/login" />
          }
        />
        <Route path="/reset-password"
          element={<ResetPassword {...sharedProps} />}
        />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/settings"
          element={<Settings session={session} {...sharedProps} />}
        />
      </Routes>
    </Router>
  );
}

export default App;
